import { Fragment, FunctionalComponent, h } from 'preact';
import { LocationType, TravelType, WeeklyFlights, WidgetTab } from '../../../common';
import { WidgetTestVariant } from '../../../config';
import TravelTypeSelector from '../../TravelTypeSelector/TravelTypeSelector';
import TravelTypeSelectorWithRadio from '../../TravelTypeSelectorWithRadio/TravelTypeSelectorWithRadio';
import { LocationSelector } from '../../LocationSelector/LocationSelector';
import LocationSwitcher from '../../LocationSwitcher/LocationSwitcher';
import PaxAmountSelector from '../../PaxAmountSelector/PaxAmountSelector';
import TravelClassSelector from '../../TravelClassSelector/TravelClassSelector';
import { DateSelector, TravelDateType } from '../../DateSelector/DateSelector';
import DateSelectorSeparator from '../../DateSelectorSeparator/DateSelectorSeparator';
import StartingPrice from '../../StartingPrice/StartingPrice';
import ContinueButton from '../../ContinueButton/ContinueButton';
import AirCalendar from '../../AirCalendar/AirCalendar';
import { PromoCodeButton } from '../../PromoCode';
import { BookingState, connectStore } from '../../../store';
import { MultiCityLayout } from '../../MultiCityLayout/MultiCityLayout';

export interface FlightContentProps {
  isOneWay: boolean;
  setFocusElement: (elem: HTMLElement) => void;
  isAward?: boolean;
  testVariant: WidgetTestVariant;
}

interface StoreProps {
  enablePromoCode: boolean;
  currentTab: WidgetTab;
  enableCompactWidget: boolean;
  directFlightsNotAvailable: boolean;
  weeklyFlights: WeeklyFlights[];
  travelType: TravelType;
}

type EffectiveProps = FlightContentProps & StoreProps;

const mapStateToProps = ({
  enablePromoCode,
  currentTab,
  enableCompactWidget,
  price,
  selection,
}: BookingState): StoreProps => ({
  enablePromoCode,
  currentTab,
  enableCompactWidget,
  directFlightsNotAvailable: price.weeklyFlights?.length === 0,
  weeklyFlights: price.weeklyFlights,
  travelType: selection.travelType,
});

export const FlightContent: FunctionalComponent<FlightContentProps> = connectStore({
  mapStateToProps,
})(
  ({
    isOneWay,
    setFocusElement,
    isAward,
    enablePromoCode,
    currentTab,
    testVariant,
    enableCompactWidget,
    travelType,
  }: EffectiveProps) => {
    if (enableCompactWidget && travelType === TravelType.MULTI_CITY) {
      return <MultiCityLayout setFocusElement={setFocusElement} />;
    }

    return (
      <Fragment>
        <AirCalendar />

        {enableCompactWidget ? (
          <TravelTypeSelector
            class="bw-travelType travelType white-bg border contentItem rounded"
            data-test-traveltype-selector
          />
        ) : (
          <TravelTypeSelectorWithRadio class="bw-travelType travelType" data-test-traveltype-selector />
        )}

        <div className={'locations white-bg border rounded'}>
          <LocationSelector locationType={LocationType.ORIGIN} isAward={isAward} testVariant={testVariant} />
          <LocationSwitcher class="bw-location-switch contentSeparator lightButton" data-test-location-switcher />
          <LocationSelector locationType={LocationType.DESTINATION} isAward={isAward} testVariant={testVariant} />
        </div>

        <PaxAmountSelector class="bw-pax-amount passengers contentItem white-bg border rounded" />

        {!enableCompactWidget && (
          <TravelClassSelector class="bw-travel-class travelClass contentItem white-bg border rounded" />
        )}

        <div className={'travelDates white-bg border rounded'}>
          <DateSelector
            class={`bw-departure departure contentItem${isOneWay ? ' singleItem' : ''}`}
            type={TravelDateType.DEPARTURE}
            isAward={isAward}
          />

          {!isOneWay && (
            <DateSelectorSeparator class="contentSeparator white-bg nordic-blue-900-text" data-test-date-separator />
          )}

          {!isOneWay && (
            <DateSelector
              class="bw-return return contentItem"
              type={TravelDateType.RETURN}
              data-test-return-date-selector
              isAward={isAward}
            />
          )}
        </div>

        <div class="bw-promo promo">
          <div class="wrap fill">
            {enablePromoCode && currentTab === WidgetTab.FLIGHT && (
              <PromoCodeButton class="bw-promoCode promoCode flex flex--middle promo-code-cta mr-small-t" />
            )}
          </div>
        </div>

        <div class="bw-cta callToAction">
          {!isAward && <StartingPrice class="totalPrice" data-test-starting-price />}
          <ContinueButton
            class="submit"
            ref={(elem: ContinueButton) => elem && setFocusElement(elem?.base as HTMLElement)}
          />
        </div>

        {enablePromoCode && currentTab === WidgetTab.FLIGHT ? (
          <PromoCodeButton class="promo-code-cta-horizontal" />
        ) : null}
      </Fragment>
    );
  }
);
